import {
  DECIDE_KEY,
  OTHER_KEY,
  QUESTION_STATE_TEXT,
  type PhaseCheckDto,
  type PhaseCommonDto,
  type PhaseFigureDto,
  type PhaseLayerCountDto,
  type PhaseLevelDto,
  type PhaseQuestionDto,
  type PhaseQuestionsDto,
  type PhaseScrapDto,
  type PhaseTalkDto,
  type StepTimelineDto,
  type WizardStepDto,
} from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { clock, Level, spent } from './Sinapsi';

/**
 * I pezzi comuni delle pagine di fase nello stile Sinapsi (docs/mockup/percorso-sinapsi/03-06; contratto n. 1 versione
 * 2.0; ADR 0171, 0174): la testa con il livello e ciò che manca, «Come l'ho preparata», i bloccanti, i numeri, le domande
 * del registro nella forma dell'art. 7-bis.4, lo scarto, «da dove viene», «Parliamone», «Non va bene» e «Confermo la
 * fase», il registro intero. La pagina non mostra i rimandi al contratto (art. 4.1).
 */

const day = (iso: string) => new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** La testa della fase: «FASE 1 · L'IMPRESA · FATTA IN 0:34», il titolo, il livello con il passo che manca. */
export function PhaseHead({
  eyebrow,
  title,
  level,
  big = false,
  ultimo,
  consegnata,
}: {
  eyebrow: string;
  title: string;
  level: PhaseLevelDto | null;
  big?: boolean;
  /** L'ultimo giro non è riuscito: la pagina mostra la consegna di prima e lo dice, con la causa. */
  ultimo?: PhaseCommonDto['ultimo_giro'];
  consegnata?: string | null;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: big ? 10 : 8 }}>
      <p className="sn-ey cyan">{eyebrow}</p>
      {ultimo && (
        <p className="sn-note" role="status" style={{ color: '#ffd68c', margin: 0 }}>
          L’ultimo tentativo non è riuscito ({ultimo.causa}){consegnata ? `: vedi la consegna del ${day(consegnata)}` : ''}.
        </p>
      )}
      <h1 className="sn-h1" style={big ? { fontSize: 40, lineHeight: 1.05 } : { fontSize: 30, fontWeight: 700, lineHeight: 1.15 }}>
        {title}
      </h1>
      {level && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Level value={level.valore} />
          <span style={{ fontSize: 14, color: '#c3cbe0' }}>
            <b style={{ color: '#eaf0ff' }}>
              Livello {level.valore} su 5, {level.nome}.
            </b>{' '}
            {level.prossimo ?? ''} {level.nota ?? ''}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Il tempo della fase per la testa: «FATTA IN 0:34», o «AL LAVORO». Con la fase (`d`) è il tempo della consegna
 * mostrata, non quello di un giro dopo non riuscito.
 */
export function doneIn(t: StepTimelineDto | null, d?: Pick<PhaseCommonDto, 'fatta_in_s' | 'ultimo_giro'>): string {
  if (!t) return '';
  if (!t.fine) return ' · Al lavoro';
  if (d?.fatta_in_s != null) return ` · Fatta in ${clock(d.fatta_in_s)}`;
  if (d?.ultimo_giro) return '';
  return ` · Fatta in ${clock(spent(t.conteggio_da ?? t.inizio, t.fine, 0))}`;
}

/** «Come l'ho preparata» (art. 10.4): il contatore chiuso di una fase finita, con il tempo della fase e dell'ondata. */
export function Prepared({ t, ondata }: { t: StepTimelineDto; ondata?: { nome: string; massimo_s: number; sedi: number } }) {
  const total = spent(t.conteggio_da ?? t.inizio, t.fine, Date.now());
  const top = t.voci.filter((v) => !v.padre && v.inizio);
  return (
    <details className="sn-prep">
      <summary>
        <span className="ttl">COME L’HO PREPARATA</span>
        <span className={`tot ${total > t.massimo_s ? 'over' : ''}`}>
          {clock(total)} ·{' '}
          <span>
            TEMPO MASSIMO {clock(t.massimo_s)}
            {ondata ? ` · ONDATA ${ondata.nome} ${clock(ondata.massimo_s)}${ondata.sedi > 1 ? `, CON ${ondata.sedi} SEDI` : ''}` : ''}
          </span>
        </span>
      </summary>
      <div className="rows">
        {top.map((v) => (
          <span key={v.id} style={{ display: 'contents' }}>
            <span className={`sn-dot ${v.esito === 'errore' ? 'red' : 'ok'}`} style={{ margin: '0 auto' }} />
            <span className="l">{v.etichetta}</span>
            <span className={`t ${v.esito === 'errore' ? 'err' : ''}`}>{clock(spent(v.inizio, v.fine, Date.now()))}</span>
          </span>
        ))}
      </div>
    </details>
  );
}

/** I bloccanti della fase, controllati dallo studio (art. 9.1). */
export function Checks({ list }: { list: PhaseCheckDto[] }) {
  if (!list.length) return null;
  return (
    <div className="sn-pills">
      {list.map((b) => (
        <span key={b.cosa} className={`sn-pill ${b.ok ? '' : 'no'}`} style={{ textTransform: 'uppercase' }}>
          {b.cosa}
        </span>
      ))}
    </div>
  );
}

const tone = (s: string) => (/· T\b|titolare/i.test(s) ? 'ok' : /scelta di Timonio|stima|confermare|non torna/i.test(s) ? 'gold' : /· A\b|visura|atto/i.test(s) ? '' : 'mute');

/** I numeri in testa alla fase, con la fonte o «da confermare». */
export function Figures({ list }: { list: PhaseFigureDto[] }) {
  if (!list.length) return null;
  return (
    <section className="sn-figs">
      {list.map((f) => {
        const kind = /^[\d\s.+]+$/.test(f.valore) ? '' : /^\d{2}\.\d{2}/.test(f.valore) ? 'code' : f.valore.length > 24 ? 'txt' : 'mid';
        return (
          <div key={f.etichetta} className={`sn-fig ${f.da_confermare ? 'gold' : ''}`}>
            <p className="k">{f.etichetta}</p>
            <p className={`v ${kind}`}>{f.valore}</p>
            <p className={`s ${f.da_confermare ? 'mute' : tone(f.fonte)}`}>{f.fonte}</p>
          </div>
        );
      })}
    </section>
  );
}

/** Lo scarto della fase (art. 8, «Lo scarto nasce dove nasce la voce»). */
export function Scrap({ list, intro }: { list: PhaseScrapDto[]; intro: string }) {
  if (!list.length) return null;
  return (
    <section className="sn-panel gold" style={{ gap: 10, padding: '20px 24px' }}>
      <h2 className="sn-h2">Lo scarto di questa fase</h2>
      <p className="sn-note" style={{ fontSize: 14 }}>
        {intro}
      </p>
      <div className="sn-scrap">
        {list.map((x, k) => (
          <span key={k} style={{ display: 'contents' }}>
            <span>
              {x.voce}: {x.testo}
            </span>
            <span className={`o ${x.oggi === 'nessuno' || x.oggi === 'ha' ? 'ok' : ''}`}>{x.oggi === 'nessuno' ? 'Nessuno scarto' : x.oggi === 'ha' ? 'C’è' : x.oggi === 'coperta' ? 'Coperta da altri' : 'Manca'}</span>
          </span>
        ))}
      </div>
    </section>
  );
}

/** «Da dove viene»: quante voci per strato. */
export function Origins({ list }: { list: PhaseLayerCountDto[] }) {
  if (!list.length) return null;
  return (
    <div className="sn-origin">
      <span>Da dove viene:</span>
      {list.map((x) => (
        <span key={x.strato}>
          {x.strato} · {x.voci}
        </span>
      ))}
    </div>
  );
}

/** Cosa è cambiato, dopo una risposta, una correzione o «Non va bene» (art. 7-bis.6). */
export function Changed({ list, missing = [] }: { list: string[]; missing?: string[] }) {
  return (
    <>
      {list.length > 0 && (
        <div className="sn-why" style={{ fontSize: 13 }}>
          <b>Cosa è cambiato.</b> {list.join(' ')}
        </div>
      )}
      {missing.length > 0 && (
        <div className="sn-why gold" style={{ fontSize: 13 }}>
          <b>Cosa manca.</b> {missing.join(' ')}
        </div>
      )}
    </>
  );
}

/**
 * Le domande di una fase (art. 7-bis.4): nascono da una voce, dicono cosa cambia, hanno la risposta predefinita accesa,
 * «altro» con lo spazio libero e «Basta, decidi tu». Niente aspetta le risposte.
 */
export function Questions({ domande, limiti, intro, onChange, empty }: { domande: PhaseQuestionDto[]; limiti: PhaseQuestionsDto['limiti']; intro: string; onChange: () => void; empty?: string }) {
  const { t, isManager } = useTenant();
  const list = domande.filter((q) => q.stato !== 'superata');
  const resolved = domande.filter((q) => q.stato === 'superata');
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h2 className="sn-h2">Cosa chiedo a te</h2>
        <p className="sn-note" style={{ fontSize: 14 }}>
          {intro} {list.length} {list.length === 1 ? 'domanda' : 'domande'} su {limiti.per_fase} in questa fase, {limiti.usate_incarico} su {limiti.per_incarico} nell’incarico.
        </p>
      </div>
      {list.map((q) => (
        <Question key={q.id} q={q} canAnswer={isManager} send={(body) => api<PhaseQuestionsDto>(t(`/onboarding/domande/${q.id}/risposta`), { method: 'POST', body }).then(onChange)} />
      ))}
      {!list.length && <p className="sn-note">{empty ?? 'Nessuna domanda per te in questa fase: le fonti dicono tutto ciò che serve.'}</p>}
      {resolved.length > 0 && <p className="sn-note">{resolved.map((q) => `«${q.domanda}» non serve più: ${q.superata_perche ?? 'una fonte ha dato la risposta'}.`).join(' ')}</p>}
    </section>
  );
}

export function Question({ q, canAnswer, send }: { q: PhaseQuestionDto; canAnswer: boolean; send: (body: { chiave: string; chiavi?: string[]; testo?: string; valori?: string[] }) => Promise<void> }) {
  const saved = q.risposta ? (q.risposta.chiavi ?? [q.risposta.chiave]) : null;
  const answered = q.stato === 'risposta' ? saved : null;
  const defaults = q.risposte.filter((o) => o.predefinita).map((o) => o.chiave);
  const [chosen, setChosen] = useState<string[]>(answered ?? (q.stato === 'scelta_timonio' || q.stato === 'non_chiesta' ? (saved ?? defaults) : defaults));
  const [free, setFree] = useState<string[]>(q.risposta?.valori ?? (q.risposta?.testo && q.risposta.chiave === OTHER_KEY ? [q.risposta.testo] : []));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pick = (k: string) => {
    setTouched(true);
    if (!q.multipla || k === DECIDE_KEY) return setChosen([k]);
    setChosen((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c.filter((x) => x !== DECIDE_KEY), k]));
  };
  const options = q.risposte.filter((o) => chosen.includes(o.chiave));
  const needsText = options.some((o) => o.chiave === OTHER_KEY || !!o.campi?.length);
  const save = async () => {
    if (!chosen.length) return;
    setBusy(true);
    setErr(null);
    try {
      const withFields = options.find((o) => o.campi?.length && o.chiave !== OTHER_KEY);
      await send(
        chosen[0] === DECIDE_KEY
          ? { chiave: DECIDE_KEY }
          : { chiave: chosen[0]!, ...(q.multipla ? { chiavi: chosen } : {}), ...(needsText ? (withFields && !q.multipla ? { valori: free } : { testo: free.filter(Boolean).join(' · ') }) : {}) },
      );
      setTouched(false);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const wide = q.risposte.length >= 4 && !q.risposte.some((o) => o.campi && o.campi.length > 1);
  const status =
    q.stato === 'risposta' && q.risposta
      ? `Risposto da te, grado T · ${day(q.risposta.il)}`
      : q.stato === 'scelta_timonio' && q.risposta
        ? `Scelta di Timonio: «${q.risposta.testo ?? ''}». Puoi rispondere quando vuoi.`
        : q.stato === 'non_chiesta'
          ? 'Non chiesta (oltre il limite delle domande): vale la risposta predefinita, come scelta di Timonio. Puoi rispondere quando vuoi.'
          : null;
  return (
    <fieldset className={`sn-q ${q.contraddizione ? 'gold' : ''}`}>
      <legend>
        {q.contraddizione ? q.voce : `Voce: ${q.voce}`} · {q.cambia}
      </legend>
      <p className="qt">
        {q.domanda}
        {q.multipla ? ' Puoi sceglierne più di una.' : ''}
      </p>
      {q.esempio && (
        <p className="sn-note" style={{ fontStyle: 'italic' }}>
          Per esempio: {q.esempio}
        </p>
      )}
      {q.spiegazione && <p className="sn-note">{q.spiegazione}</p>}
      {status && <p className="sn-answered">{status}</p>}
      <div className={wide ? 'grid2' : ''} style={wide ? undefined : { display: 'flex', flexDirection: 'column', gap: 10 }}>
        {q.risposte.map((o) => {
          const on = chosen.includes(o.chiave);
          return (
            <label key={o.chiave} className={`sn-opt ${on ? 'on' : ''}`}>
              <input type={q.multipla && o.chiave !== DECIDE_KEY ? 'checkbox' : 'radio'} name={q.id} checked={on} disabled={!canAnswer} onChange={() => pick(o.chiave)} />
              <span style={{ minWidth: 0 }}>
                <span className="ot">{o.testo}</span>
                {o.effetto && <span className="oe">{o.effetto}</span>}
                {o.predefinita && (
                  <span className="oe" style={{ color: '#c3cbe0' }}>
                    Risposta usata se non rispondi.
                    {q.perche_predefinita ? ` Perché: ${q.perche_predefinita}` : ''}
                    {q.fonte_predefinita ? ` · Fonte: ${q.fonte_predefinita}` : ''}
                  </span>
                )}
                {on && (o.chiave === OTHER_KEY || !!o.campi?.length) && (
                  <span className="free">
                    {(o.campi?.length ? o.campi : ['La tua risposta']).map((c, i) => (
                      <input
                        key={c}
                        type="text"
                        className={o.campi && o.campi.length > 1 ? 'short' : ''}
                        placeholder={c}
                        aria-label={c}
                        value={free[i] ?? ''}
                        disabled={!canAnswer}
                        onChange={(e) => {
                          setTouched(true);
                          setFree((f) => {
                            const n = [...f];
                            n[i] = e.target.value;
                            return n;
                          });
                        }}
                      />
                    ))}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
      {canAnswer && (
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="sn-btn sm" disabled={busy || !chosen.length || (q.stato === 'risposta' && !touched)} onClick={() => void save()}>
            {busy ? 'Salvo…' : q.stato === 'risposta' ? 'Cambia la risposta' : 'Salva la risposta'}
          </button>
          <span className="sn-note">La risposta è tua: resta nel fascicolo con la data e la puoi cambiare quando vuoi.</span>
          {err && <span className="sn-err">{err}</span>}
        </div>
      )}
    </fieldset>
  );
}

/**
 * «Parliamone» (art. 7-bis.14): il titolare scrive con parole sue; il consulente restituisce «ho capito così» (la voce o
 * la domanda toccata, il fatto inteso, cosa cambierebbe) e aspetta il sì. Fino al sì nulla cambia.
 */
export function Talk({ fase }: { fase: number }) {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const list = useTenantQuery<PhaseTalkDto[]>(['parliamone', fase], `/onboarding/parliamone?fase=${fase}`, { refetchInterval: (d) => (d?.some((x) => x.stato === 'in_lettura') ? 2000 : false) });
  const [text, setText] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  const write = useMutation({
    mutationFn: (testo: string) => api<PhaseTalkDto>(t('/onboarding/parliamone'), { method: 'POST', body: { fase, testo } }),
    onSuccess: () => {
      setText('');
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'parliamone', fase] });
    },
  });
  const reply = useMutation({
    mutationFn: ({ id, esito }: { id: string; esito: 'si' | 'lascia' }) => api<PhaseTalkDto>(t(`/onboarding/parliamone/${id}`), { method: 'POST', body: { esito } }),
    onSuccess: () => void refresh(),
  });
  const last = list.data?.[0] ?? null;
  // «Correggi» su una voce apre «Parliamone» con il testo già iniziato.
  useEffect(() => {
    const on = (e: Event) => {
      setText((e as CustomEvent<string>).detail);
      document.getElementById('parliamone')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    window.addEventListener('sn-talk', on);
    return () => window.removeEventListener('sn-talk', on);
  }, []);
  return (
    <section className="sn-talk" id="parliamone">
      <h2 className="sn-h2">Parliamone</h2>
      <p className="sn-note" style={{ fontSize: 14 }}>
        Scrivi quello che vuoi dire sull’azienda, con parole tue. Non cambio nulla finché non mi dici di sì.
      </p>
      {last && (
        <div className="said">
          <span className="who">Hai scritto · {day(last.il)}</span>
          {last.testo}
        </div>
      )}
      {last?.stato === 'in_lettura' && (
        <p className="sn-note">
          <span className="sn-dot spin" style={{ verticalAlign: -1, marginRight: 8 }} />
          Leggo ciò che hai scritto, con davanti tutto il progetto e le domande…
        </p>
      )}
      {last?.stato === 'fuori_progetto' && <p className="sn-note">Questo non lo tratta questo incarico: non registro nulla.</p>}
      {last?.stato === 'errore' && <p className="sn-err">Non sono riuscito a leggerlo adesso: riscrivilo tra poco.</p>}
      {last && (last.stato === 'da_confermare' || last.stato === 'confermato') && (
        <div className="got">
          <span className="sn-ey">{last.stato === 'confermato' ? 'Ho capito così · confermato' : 'Ho capito così'}</span>
          {last.righe.map((r, k) => (
            <div key={k} className="row">
              <span className="k">VOCE</span>
              <span>{r.voce}</span>
              <span className="k">HO INTESO</span>
              <span>{r.fatto}</span>
              <span className="k">CAMBIEREBBE</span>
              <span className="c">{r.cosa_cambierebbe}</span>
            </div>
          ))}
          {last.stato === 'da_confermare' && isManager && (
            <>
              <div className="btns">
                <button type="button" className="sn-btn sm" style={{ fontSize: 13, padding: '10px 18px', minHeight: 42 }} disabled={reply.isPending} onClick={() => reply.mutate({ id: last.id, esito: 'si' })}>
                  {reply.isPending ? 'Registro…' : 'Sì, è così'}
                </button>
                <button
                  type="button"
                  className="sn-btn ghost sm"
                  style={{ fontSize: 13, padding: '10px 14px', minHeight: 42 }}
                  disabled={reply.isPending}
                  onClick={() => {
                    setText(last.testo);
                    reply.mutate({ id: last.id, esito: 'lascia' });
                  }}
                >
                  Lo riscrivo
                </button>
                <button type="button" className="sn-btn ghost sm" style={{ fontSize: 13, padding: '10px 14px', minHeight: 42 }} disabled={reply.isPending} onClick={() => reply.mutate({ id: last.id, esito: 'lascia' })}>
                  Lascia stare
                </button>
              </div>
              <p className="sn-note">Con il sì diventa una tua risposta (T) con la data e il tuo testo; poi ti dico cosa è cambiato e dove.</p>
            </>
          )}
          {last.stato === 'confermato' && last.cambiato.length > 0 && (
            <p className="sn-note">
              <b style={{ color: '#46e0b0' }}>Fatto.</b> {last.cambiato.join(' ')}
            </p>
          )}
        </div>
      )}
      {isManager && (
        <>
          <textarea aria-label="Scrivi" placeholder="Per esempio: questa cosa da noi funziona diversamente…" value={text} onChange={(e) => setText(e.target.value)} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="sn-btn ghost sm" style={{ fontSize: 13 }} disabled={text.trim().length < 2 || write.isPending} onClick={() => write.mutate(text.trim())}>
              {write.isPending ? 'Invio…' : 'Invia'}
            </button>
            {write.error && <span className="sn-err">{errorMessage(write.error)}</span>}
          </div>
        </>
      )}
    </section>
  );
}

/** Apre «Parliamone» con un testo già iniziato (es. «Correggi» su una sede). */
export const talkAbout = (text: string) => window.dispatchEvent(new CustomEvent('sn-talk', { detail: text }));

/** In fondo alla fase: «Non va bene» con il motivo (una volta), «Confermo la fase», «Avanti». */
export function PhaseFooter({
  rifatta,
  confermata,
  canConfirm,
  onRedo,
  onConfirm,
  next,
  onNext,
  sealing,
  extra,
  confirmLabel = 'Confermo la fase',
}: {
  rifatta: boolean;
  confermata: string | null;
  canConfirm: boolean;
  onRedo: (motivo: string) => Promise<unknown>;
  onConfirm: () => Promise<unknown>;
  next: WizardStepDto | null;
  onNext: () => void;
  sealing: boolean;
  extra?: ReactNode;
  /** Il testo del pulsante di conferma (la fase 11: «Confermo e chiudo l'incarico», art. 12.2). */
  confirmLabel?: string;
}) {
  const { isManager } = useTenant();
  const [why, setWhy] = useState<string | null>(null);
  const [busy, setBusy] = useState<'redo' | 'confirm' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const run = async (k: 'redo' | 'confirm', fn: () => Promise<unknown>) => {
    setBusy(k);
    setErr(null);
    try {
      await fn();
      if (k === 'redo') setWhy(null);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap', paddingTop: 6, alignItems: 'start' }}>
        <span style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {why === null && (
            <button type="button" className="sn-btn ghost sm" disabled={!isManager || rifatta || !!busy} onClick={() => setWhy('')} title={rifatta ? 'La fase si rifà una sola volta' : undefined}>
              Non va bene
            </button>
          )}
          <button type="button" className="sn-btn ghost sm" disabled={!isManager || !canConfirm || !!confermata || !!busy} onClick={() => void run('confirm', onConfirm)}>
            {confermata ? `Confermata il ${day(confermata)}` : busy === 'confirm' ? 'Confermo…' : confirmLabel}
          </button>
          {extra}
        </span>
        {next && (
          <button type="button" className="sn-btn sm" disabled={sealing} onClick={onNext}>
            Avanti: {next.titolo.toLowerCase()}
          </button>
        )}
      </div>
      {rifatta && <span className="sn-note">La fase è già stata rifatta una volta con il tuo motivo.</span>}
      {why !== null && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (why.trim()) void run('redo', () => onRedo(why.trim()));
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <label className="sn-note" htmlFor="motivo">
            Cosa non va? La fase si rifà una volta tenendo conto del motivo e delle tue risposte.
          </label>
          <textarea id="motivo" rows={3} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Per esempio: la sede di Modugno è solo un magazzino" />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="sn-btn sm" disabled={!why.trim() || !!busy}>
              {busy === 'redo' ? 'Rifaccio la fase…' : 'Rifai la fase'}
            </button>
            <button type="button" className="sn-btn ghost sm" onClick={() => setWhy(null)}>
              Annulla
            </button>
          </div>
        </form>
      )}
      {err && <p className="sn-err">{err}</p>}
    </div>
  );
}

/** Il registro delle domande, intero (art. 7-bis.11): ogni domanda con la fase, la voce, lo stato e la risposta. */
export function Registry({ onClose }: { onClose: () => void }) {
  const { t, isManager, tenant } = useTenant();
  const qc = useQueryClient();
  const q = useTenantQuery<PhaseQuestionsDto>(['domande'], '/onboarding/domande');
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  const d = q.data;
  return (
    <div className="sn-reg" role="dialog" aria-modal="true" aria-label="Il registro delle domande" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <h2 className="sn-h2">Il registro delle domande</h2>
          <button type="button" className="sn-btn ghost sm" style={{ fontSize: 13 }} onClick={onClose}>
            Chiudi
          </button>
        </div>
        {d && (
          <p className="sn-note" style={{ fontSize: 14 }}>
            {d.registro.aperte} aperte · {d.registro.scelte_timonio} scelte di Timonio · {d.registro.risposte} risposte da te · {d.registro.superate} superate · {d.registro.non_chieste} non chieste. {d.limiti.usate_incarico} domande su {d.limiti.per_incarico} nell’incarico. Puoi rispondere a ognuna in qualsiasi momento: la risposta aggiorna le fasi che ne dipendono, senza rifarle.
          </p>
        )}
        {!d && <p className="sn-note">Apro il registro…</p>}
        {d?.domande.map((x) => (
          <div key={x.id} className="item">
            <span className={`st ${x.stato}`}>
              Fase {x.fase} · {QUESTION_STATE_TEXT[x.stato]}
              {x.risposta ? ` · ${day(x.risposta.il)}` : ''}
              {x.grado ? ' · T' : ''}
            </span>
            <span style={{ color: '#8b97b5', fontSize: 13 }}>Voce: {x.voce}</span>
            <b>{x.domanda}</b>
            {x.risposta && <span>{x.stato === 'risposta' ? 'La tua risposta' : 'La scelta di Timonio'}: {x.risposta.testo ?? x.risposta.chiave}</span>}
            {x.stato === 'superata' && x.superata_perche && <span className="sn-note">{x.superata_perche}</span>}
            {x.stato !== 'superata' && isManager && (
              <>
                {open === x.id ? (
                  <Question
                    q={x}
                    canAnswer={isManager}
                    send={(body) =>
                      api<PhaseQuestionsDto>(t(`/onboarding/domande/${x.id}/risposta`), { method: 'POST', body }).then(() => {
                        setOpen(null);
                        void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
                      })
                    }
                  />
                ) : (
                  <button type="button" className="sn-link" style={{ alignSelf: 'flex-start' }} onClick={() => setOpen(x.id)}>
                    {x.stato === 'risposta' ? 'Cambia la risposta' : 'Rispondi'}
                  </button>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
